import React, { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Progress,
  Text,
} from '@chakra-ui/react'
import { IMicrophoneAudioTrack } from 'agora-rtc-react'
import { MicOn } from 'src/components/icon'
import { Select } from 'src/components/Select/Select'

import { useLocalPlayer } from '../../context/localPlayerContext'
import { useDeviceSelection } from '../../hooks/useDeviceSelection'
import { useMediaDevices } from '../../hooks/useMediaDevices'

const VolumeProgressBar: React.FC<{
  track: IMicrophoneAudioTrack | null
}> = ({ track }) => {
  const [volumeLevel, setVolumeLevel] = useState(0)

  useEffect(() => {
    if (!track) {
      setVolumeLevel(0)
      return
    }
    const id = setInterval(() => {
      setVolumeLevel(track.getVolumeLevel())
    }, 100)
    return () => clearInterval(id)
  }, [track])

  return (
    <Progress
      value={Math.pow(volumeLevel, 3) * 100}
      colorScheme="green"
      size="lg"
      borderRadius="md"
      min={0}
      max={100}
      sx={{ '& > div': { transition: 'width 80ms ease-out' } }}
    />
  )
}

export const MicrophoneLevelPanel: React.FC = () => {
  const {
    localMicrophoneTrack,
    localMicrophoneError,
    currentMicrophoneDeviceId,
    switchMicrophone,
    toggleLocalMicrophone,
  } = useLocalPlayer()
  const { microphoneDevices } = useMediaDevices()
  const [selectedMicId, setSelectedMicId] = useDeviceSelection(
    currentMicrophoneDeviceId,
    microphoneDevices,
    switchMicrophone,
  )

  const micItems = useMemo(
    () =>
      microphoneDevices.map(device => ({
        value: device.deviceId,
        label: device.label,
      })),
    [microphoneDevices],
  )

  const isActive = localMicrophoneTrack !== null
  const hasError = localMicrophoneError !== null

  return (
    <Card p="4">
      <Flex direction="column" gap="3">
        <HStack spacing="2">
          <Box color="gray.700">
            <MicOn />
          </Box>
          <Text fontWeight="bold" fontSize="md">
            マイク
          </Text>
        </HStack>
        {isActive ? (
          <VolumeProgressBar track={localMicrophoneTrack} />
        ) : (
          <div>
            <Button
              onClick={toggleLocalMicrophone}
              isDisabled={hasError}
              colorScheme="blue"
              variant="outline"
            >
              マイクを確認する
            </Button>
          </div>
        )}

        {microphoneDevices.length > 0 && (
          <Flex align="stretch" gap="1" direction="column" w="max-content">
            <Text fontSize="sm" color="gray.600">
              使用するマイク
            </Text>
            <Select
              value={selectedMicId}
              items={micItems}
              onChange={setSelectedMicId}
            />
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
